// ═══════════════════════════════════════════════════════════════════════════
// WRITTN - Verse Engine
// ═══════════════════════════════════════════════════════════════════════════
// Handles verse selection, filtering, and the "holy randomness" algorithm.
// ═══════════════════════════════════════════════════════════════════════════

import VERSES from '../config/versesData.js';
import VERSE_CONFIG from '../config/verses.js';
import FIRST_SESSION_VERSES from '../config/firstSession.js';
import { SETTINGS } from '../config/settings.js';
import { getSeenVerses, hasSeenFirstSession, getDivineVerseCount, incrementDivineVerseCount } from './storage.js';
import { PASSAGE_CONTEXT, getDefaultContext } from '../config/passageContext.js';

// ─────────────────────────────────────────────────────────────────────────
// VERSE POOL
// ─────────────────────────────────────────────────────────────────────────

let filteredVerses = null;
let weightedPool = null;

function getFilteredVerses() {
    if (filteredVerses) return filteredVerses;

    // Remove blocked verses
    filteredVerses = VERSES.filter(verse => {
        return !VERSE_CONFIG.blocklist.includes(verse.reference);
    });

    return filteredVerses;
}

function buildWeightedPool() {
    if (weightedPool) return weightedPool;

    const verses = getFilteredVerses();
    weightedPool = [];

    verses.forEach(verse => {
        const weight = VERSE_CONFIG.bookWeights[verse.book] || 1.0;
        // Add verse multiple times based on weight
        const count = Math.round(weight * 10);
        for (let i = 0; i < count; i++) {
            weightedPool.push(verse);
        }
    });

    return weightedPool;
}

// ─────────────────────────────────────────────────────────────────────────
// VERSE SELECTION
// ─────────────────────────────────────────────────────────────────────────

function shuffleArray(array) {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
}

export function getFirstSessionVerses() {
    return [...FIRST_SESSION_VERSES];
}

export function getRandomVerse(excludeReferences = []) {
    const pool = buildWeightedPool();
    const seenVerses = getSeenVerses();
    const allExcluded = [...excludeReferences, ...seenVerses];

    // Filter out excluded verses
    let available = pool.filter(v => !allExcluded.includes(v.reference));

    // If we've seen almost all verses, reset the seen list
    if (available.length < 10) {
        available = pool.filter(v => !excludeReferences.includes(v.reference));
    }

    if (available.length === 0) {
        available = pool;
    }

    // Pick random
    const index = Math.floor(Math.random() * available.length);
    return available[index];
}

export function getMultipleVerses(count, excludeReferences = []) {
    const verses = [];
    const excluded = [...excludeReferences];

    for (let i = 0; i < count; i++) {
        const verse = getRandomVerse(excluded);
        if (verse) {
            verses.push(verse);
            excluded.push(verse.reference);
        }
    }

    return verses;
}

// ─────────────────────────────────────────────────────────────────────────
// DIVINE VERSE
// ─────────────────────────────────────────────────────────────────────────

export function shouldShowDivineVerse() {
    if (!SETTINGS.showDivineVerse) return false;

    const count = getDivineVerseCount();
    return count >= SETTINGS.divineVerseFrequency;
}

export function getDivineVerse() {
    const pool = VERSE_CONFIG.divineVersePool;
    const seenVerses = getSeenVerses();

    // Filter to divine verses that haven't been seen
    const available = pool.filter(ref => !seenVerses.includes(ref));

    // If all divine verses seen, pick from any
    const pickFrom = available.length > 0 ? available : pool;
    const reference = pickFrom[Math.floor(Math.random() * pickFrom.length)];

    // Find the full verse
    const verse = getFilteredVerses().find(v => v.reference === reference);

    // Reset counter
    incrementDivineVerseCount();

    return verse || getRandomVerse();
}

export function trackVerseViewed() {
    incrementDivineVerseCount();
}

// ─────────────────────────────────────────────────────────────────────────
// CONTEXT / DIVE DEEPER
// ─────────────────────────────────────────────────────────────────────────

const BOOK_INTROS = {
    // Old Testament
    "Genesis": "The book of beginnings — creation, fall, and the call of Abraham to be a blessing to all nations.",
    "Exodus": "Israel's dramatic escape from Egypt, the giving of the Law at Sinai, and the building of God's tabernacle.",
    "Leviticus": "The handbook for holiness — sacrifices, purity, and festivals that shaped Israel's worship and identity.",
    "Numbers": "Forty years of wilderness wandering, testing Israel's faith and preparing them for the Promised Land.",
    "Deuteronomy": "Moses' final sermons urging Israel to love God and keep covenant before entering Canaan.",
    "Joshua": "The conquest of Canaan and the division of the land among the twelve tribes of Israel.",
    "Judges": "A cycle of sin, oppression, and deliverance through unlikely heroes during Israel's early settlement.",
    "Ruth": "A story of loyalty and redemption: a Moabite widow becomes an ancestor of King David.",
    "1 Samuel": "From the last judge Samuel to Israel's first king Saul to the rise of David.",
    "2 Samuel": "David's reign as king — his victories, his failures, and God's promise of an eternal dynasty.",
    "1 Kings": "Solomon's glory and the tragic division of Israel into two kingdoms.",
    "2 Kings": "The decline and fall of both kingdoms, ending in exile to Assyria and Babylon.",
    "1 Chronicles": "Israel's history retold through the lens of worship, focusing on David and the temple.",
    "2 Chronicles": "The temple and its worship, from Solomon's building to its destruction and hope of return.",
    "Ezra": "The return from exile and the rebuilding of the temple under Zerubbabel and Ezra.",
    "Nehemiah": "Rebuilding Jerusalem's walls and renewing the covenant under Nehemiah's leadership.",
    "Esther": "A Jewish queen saves her people from genocide in Persia — God works behind the scenes.",
    "Job": "A righteous man's suffering and his encounter with the God who is beyond our understanding.",
    "Psalms": "A collection of 150 songs and prayers expressing the full range of human emotion before God — from lament to praise, from despair to joy.",
    "Proverbs": "Ancient wisdom literature offering practical guidance for living well, collected by Israel's sages over centuries.",
    "Ecclesiastes": "The Teacher's search for meaning 'under the sun' — wrestling with life's mysteries and finding purpose in God.",
    "Song of Solomon": "A poetic celebration of love and intimacy between a bride and bridegroom.",
    "Isaiah": "The prophet speaks of judgment and hope, foretelling both Israel's exile and the coming of the Suffering Servant who would bring salvation.",
    "Jeremiah": "The weeping prophet calls Judah to repentance before the Babylonian exile, yet speaks of a new covenant written on the heart.",
    "Lamentations": "Five poems of grief over the destruction of Jerusalem, yet finding hope in God's faithfulness.",
    "Ezekiel": "Visions of God's glory departing and returning, promising restoration to dry bones and dead hearts.",
    "Daniel": "Faithfulness in exile, prophetic dreams, and visions of kingdoms rising and falling before God's eternal reign.",
    "Hosea": "A prophet's broken marriage becomes a picture of God's relentless love for unfaithful Israel.",
    "Joel": "A locust plague points to the Day of the Lord — judgment and the promise of the Spirit poured out.",
    "Amos": "The shepherd-prophet declares justice for the poor and warns the wealthy who exploit the vulnerable.",
    "Obadiah": "The shortest Old Testament book pronouncing judgment on Edom for betraying their brothers.",
    "Jonah": "A reluctant prophet discovers God's mercy extends even to Israel's enemies in Nineveh.",
    "Micah": "Justice, mercy, and humility: what the Lord requires, and the promise of a ruler from Bethlehem.",
    "Nahum": "The fall of Nineveh — the same city Jonah preached to now faces God's judgment.",
    "Habakkuk": "A prophet's dialogue with God about evil and injustice, ending in faith despite circumstances.",
    "Zephaniah": "Warning of the Day of the Lord's judgment, followed by a promise of restoration and joyful singing.",
    "Haggai": "A call to rebuild the temple and prioritize God's house after the return from exile.",
    "Zechariah": "Apocalyptic visions of Jerusalem restored and the coming of the humble king on a donkey.",
    "Malachi": "The final Old Testament prophet confronting apathy and promising Elijah before the great day of the Lord.",
    // New Testament
    "Matthew": "The first Gospel presents Jesus as the promised Messiah, the king of Israel, fulfilling the Law and the Prophets.",
    "Mark": "The fast-paced Gospel showing Jesus as the suffering servant who came to give his life as a ransom.",
    "Luke": "The beloved physician tells the story of Jesus as the Savior for all people — Jew and Gentile alike.",
    "John": "The Gospel of John presents Jesus as the divine Word made flesh, emphasizing his identity as the Son of God.",
    "Acts": "The Spirit-empowered church spreads the gospel from Jerusalem to Rome through apostolic witness.",
    "Romans": "Paul's letter to the church in Rome, laying out the gospel of grace: justification by faith, life in the Spirit, and the hope of glory.",
    "1 Corinthians": "Paul addresses divisions, immorality, and confusion about spiritual gifts in the church at Corinth.",
    "2 Corinthians": "Paul's most personal letter, defending his ministry and revealing his weakness as the place where God's power is displayed.",
    "Galatians": "Paul's passionate defense of the gospel of grace against those who would add works of the law to faith.",
    "Ephesians": "Paul's letter celebrating the mystery of the church — Jews and Gentiles united in Christ, called to walk in love.",
    "Philippians": "A letter of joy written from prison, calling believers to rejoice in the Lord and to have the mind of Christ.",
    "Colossians": "Paul's letter proclaiming the supremacy of Christ over all creation and calling believers to new life in him.",
    "1 Thessalonians": "An early letter encouraging new believers and addressing questions about the return of Christ.",
    "2 Thessalonians": "Correcting confusion about the Day of the Lord and calling for faithful endurance.",
    "1 Timothy": "Paul's instruction to his young co-worker on leading the church and guarding sound doctrine.",
    "2 Timothy": "Paul's final letter from prison, passing the torch to Timothy and urging faithful endurance.",
    "Titus": "Practical guidance for Titus in organizing churches and promoting good works on the island of Crete.",
    "Philemon": "A personal letter asking Philemon to receive his runaway slave Onesimus as a brother in Christ.",
    "Hebrews": "An anonymous sermon declaring the supremacy of Christ over all things — prophets, angels, Moses, and the old covenant itself.",
    "James": "Practical wisdom for Christian living, emphasizing that genuine faith produces good works.",
    "1 Peter": "A letter of encouragement to suffering believers, calling them to hope in Christ and to live as strangers in the world.",
    "2 Peter": "A warning against false teachers and a call to grow in the knowledge of Christ.",
    "1 John": "A letter of assurance exploring the themes of light, love, and life — and what it means to know God.",
    "2 John": "A brief letter warning against false teachers and urging believers to walk in truth and love.",
    "3 John": "A note commending hospitality to traveling missionaries and warning against divisive leaders.",
    "Jude": "A short letter contending for the faith against those who pervert grace into license.",
    "Revelation": "The apocalyptic vision of John, revealing the ultimate victory of God and the Lamb over evil, and the coming of the new creation.",
};

export function getBookIntro(book) {
    return BOOK_INTROS[book] || "A book of the Bible, part of God's unfolding story of creation, redemption, and restoration.";
}

export function getSurroundingContext(verse) {
    // Parse the reference to find nearby verses
    const match = verse.reference.match(/^(.+?) (\d+):(\d+)/);
    if (!match) return [];

    const [, book, chapter, verseNum] = match;
    const num = parseInt(verseNum, 10);

    // Find verses from same chapter
    const sameChapter = getFilteredVerses().filter(v => {
        const m = v.reference.match(/^(.+?) (\d+):(\d+)/);
        if (!m) return false;
        return m[1] === book && m[2] === chapter && v.reference !== verse.reference;
    });

    // Sort by verse number
    sameChapter.sort((a, b) => {
        const aNum = parseInt(a.reference.match(/:(\d+)/)?.[1] || 0, 10);
        const bNum = parseInt(b.reference.match(/:(\d+)/)?.[1] || 0, 10);
        return aNum - bNum;
    });

    return sameChapter.slice(0, 3);
}

// ─────────────────────────────────────────────────────────────────────────
// PASSAGE CONTEXT (±5 Surrounding Verses)
// ─────────────────────────────────────────────────────────────────────────

export function getPassageContext(verse) {
    // Try to find pre-computed context for this verse reference
    const context = PASSAGE_CONTEXT[verse.reference];

    if (context) {
        return {
            chapter: context.chapter,
            before: context.before || [],
            current: context.current,
            after: context.after || []
        };
    }

    // Fallback: return minimal context from our curated list
    const surrounding = getSurroundingContext(verse);

    // Parse verse number from reference
    const match = verse.reference.match(/:(\d+)(?:-(\d+))?/);
    const verseNums = match ? (match[2] ? `${match[1]}-${match[2]}` : match[1]) : "1";

    return {
        chapter: null,
        before: [],
        current: { num: verseNums, nums: verseNums, text: verse.text },
        after: surrounding.map(v => {
            const vMatch = v.reference.match(/:(\d+)/);
            return { num: vMatch ? vMatch[1] : "?", text: v.text };
        })
    };
}

// ─────────────────────────────────────────────────────────────────────────
// EXPORT
// ─────────────────────────────────────────────────────────────────────────

export default {
    getFirstSessionVerses,
    getRandomVerse,
    getMultipleVerses,
    shouldShowDivineVerse,
    getDivineVerse,
    trackVerseViewed,
    getBookIntro,
    getSurroundingContext,
    getPassageContext,
};
